import { useState, useEffect, useCallback } from "react";

export function usePWA({ watchVersion = false } = {}) {
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    // Check if already installed
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setIsInstalled(isStandalone);

    // Capture install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
    };

    // Detect app installed
    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setIsInstalled(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Register service worker
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          setRegistration(reg);
          reg.addEventListener("updatefound", () => {
            const newWorker = reg.installing;
            if (newWorker) {
              newWorker.addEventListener("statechange", () => {
                if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
                  setUpdateAvailable(true);
                }
              });
            }
          });
        })
        .catch(() => {});
    }

    // New build detector: a deploy changes the hashed main bundle; the open tab would otherwise hit missing chunks
    const currentMain = () => { const s = document.querySelector('script[src*="/static/js/main."]'); return s ? new URL(s.src, window.location.origin).pathname : null; };
    const checkVersion = async () => {
      const mine = currentMain();
      if (!mine || document.visibilityState === "hidden") return;
      try {
        const r = await fetch(`/asset-manifest.json?_=${Date.now()}`, { cache: "no-store" });
        if (!r.ok) return;
        const live = (await r.json())?.files?.["main.js"];
        if (live && new URL(live, window.location.origin).pathname !== mine) setUpdateAvailable(true);
      } catch { /* offline — try again later */ }
    };
    const timer = watchVersion ? setInterval(checkVersion, 5 * 60 * 1000) : null;
    const onVisible = () => { if (document.visibilityState === "visible") checkVersion(); };
    if (watchVersion) { document.addEventListener("visibilitychange", onVisible); setTimeout(checkVersion, 15000); }

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      window.removeEventListener("appinstalled", handleAppInstalled);
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [watchVersion]);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) return false;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    setInstallPrompt(null);
    return outcome === "accepted";
  }, [installPrompt]);

  const applyUpdate = useCallback(() => {
    if (registration?.waiting) registration.waiting.postMessage({ type: "SKIP_WAITING" });
    sessionStorage.removeItem("chunk_reload_at");
    window.location.reload();
  }, [registration]);

  return {
    canInstall: !!installPrompt && !isInstalled,
    isInstalled,
    updateAvailable,
    promptInstall,
    applyUpdate,
  };
}
